import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RecordingStatus, useAudioRecording } from '../../context/AudioRecordingContext';
import { useApiReload } from '../../context/apiRelaod';
import { useAuth } from '../../context/authContext';
import { tokenVerfication } from "../../services/apiService";
import useAddNote from '../../services/audioNote';
import { blobToFile } from '../../utils/audio.util';
import { getAudioBlob } from '../../utils/indexedDB';

interface MyAudioRecorderProps {
  selectedNote?: string;
}

export interface MyAudioRecorderHandle {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<boolean>; // Return true if successful
  pauseResume: () => Promise<void>;
  deleteRecording: () => Promise<void>;
}

const MyAudioRecorder = forwardRef<MyAudioRecorderHandle, MyAudioRecorderProps>(
  ({ selectedNote }, ref) => {
    const navigate = useNavigate();
    const { caseInfo, patientID, setIsOnNewNoteScreen } = useApiReload();
    const { addNote } = useAddNote();
    const { call } = tokenVerfication();
    const { onLogout } = useAuth();

    // Use the centralized recording context with dictionary-based approach
    const {
      recordings,
      activeRecordingId,
      startRecording,
      pauseRecording,
      resumeRecording,
      stopRecording,
      deleteRecording,
      updateNoteType,
      getRecordingStatus
    } = useAudioRecording();

    // Token verification
    useEffect(() => {
      const subscription = call().subscribe({
        next: (res: any) => {
          if (res?.status === 204) {
            console.log("Token verification successful");
          }
        },
        error: (e: any) => {
          if (e?.status === 409) {
            onLogout(e?.status);
          }
        }
      });

      return () => subscription.unsubscribe();
    }, [activeRecordingId]);

    // Get current patient and case IDs
    const currentPatientId = patientID?.id || 'Anonymous';
    const currentSoapId = caseInfo?.id || '-1';
    const recordingId = `${currentPatientId}:${currentSoapId}`;

    // Update selectedNote if it changes
    useEffect(() => {
      if (recordings[recordingId] && selectedNote &&
          recordings[recordingId].SoapType !== selectedNote) {
        updateNoteType(recordingId, selectedNote);
      }
    }, [selectedNote, recordings[recordingId]?.SoapType]);

    // Handle starting a new recording
    const handleStartRecording = async () => {
      try {
        // Check microphone permissions
        await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter((device) => device.kind === 'audioinput');

        if (microphones.length <= 0) {
          toast.error('No microphone is plugged in!');
          return;
        }

        // Limit to 3 recordings at a time
        if (Object.keys(recordings).length >= 3 && !recordings[recordingId]) {
          toast.error('You can only record 3 audios at a time');
          setIsOnNewNoteScreen(false);
          return;
        }

        // Start recording with patient info
        await startRecording({
          patientId: currentPatientId,
          patientName: `${patientID?.firstName || 'Anonymous'} ${patientID?.lastName || 'patient'}`,
          soapId: currentSoapId,
          soapType: selectedNote || 'INITIAL_NOTE'
        });

        setIsOnNewNoteScreen(true);

      } catch (err: any) {
        if (err.name === 'NotAllowedError') {
          toast.error('Permission to access the microphone was denied.');
        } else if (err.name === 'NotFoundError') {
          toast.error('No microphone found. Please plug one in.');
        } else {
          toast.error('An unexpected error occurred: ' + err.message);
        }
      }
    };

    // Handle stopping the recording and uploading
    const handleStopRecording = async (): Promise<boolean> => {
      const recording = recordings[recordingId];

      if (!recording) {
        return false;
      }

      try {
        // Try to stop recording - will fail if less than 3 minutes
        const stopResult = await stopRecording(recordingId);
        if (!stopResult) {
          return false;
        }

        // Upload if stop was successful
        const audioBlob = await getAudioBlob(recording);
        if (audioBlob) {
          const webmFile = blobToFile(audioBlob);

          // Return a promise that resolves when the API call completes
          return new Promise((resolve) => {
            // Upload to server
            addNote({
              patientId: recording.patientId,
              caseId: recording.Soapid,
              noteType: recording.SoapType,
              file: webmFile
            }).subscribe({
              next: (res: any) => {
                // Delete local copy after successful upload
                deleteRecording(recordingId);
                toast.success(res?.msg);

                // Navigate back
                if (recording.patientId === 'Anonymous') {
                  navigate("/");
                } else {
                  navigate(-1);
                }

                resolve(true);
              },
              error: (e: any) => {
                toast.error(e?.data?.msg || e.message);
                resolve(false);
              },
            });
          });
        }

        return false;
      } catch (error) {
        console.error("Error in handleStopRecording:", error);
        return false;
      }
    };

// maximum limit of recordings
    const recordingTime= getRecordingStatus(currentPatientId, currentSoapId);
    useEffect(() => {
      const handleRecordingTime = async () => {
      if (recordingTime?.time === 3000) {
          toast.info('You have 10 minutes left to record. The recording will be uploaded automatically after this.');
      }
      if (recordingTime?.time === 3300) {
       toast.info('You have 5 minutes left to record. The recording will be uploaded automatically after this.');

      }
      if (recordingTime?.time === 3600) {
        const toastid = "limit-finished"
          toast.info('Recording time is up. Uploading the recording now...',{toastId:toastid});
          await   handleStopRecording()

      }}
      handleRecordingTime();

    }, [recordingTime.time]);


    // Handle pausing/resuming recording
    const handlePauseResume = async () => {
      try {
        const recording = recordings[recordingId];

        if (!recording) {
          toast.error('No recording found for this patient');
          return;
        }

        if (recording.status === RecordingStatus.RECORDING) {
          // If recording, pause it
          await pauseRecording(recordingId);
          setIsOnNewNoteScreen(false);
        } else if (recording.status === RecordingStatus.PAUSED) {
          // If paused, resume it
          await resumeRecording(recordingId);
          setIsOnNewNoteScreen(true);
        }
      } catch (error) {
        console.error("Error in handlePauseResume:", error);
        toast.error("Failed to pause/resume recording");
      }
    };

    // Handle deleting recording
    const handleDeleteRecording = async () => {
      try {
        if (!recordings[recordingId]) {
          toast.error('No recording found for this patient');
          return;
        }

        await deleteRecording(recordingId);
        setIsOnNewNoteScreen(false);

      } catch (error) {
        console.error("Error in handleDeleteRecording:", error);
        toast.error("Failed to delete recording");
      }
    };

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      startRecording: handleStartRecording,
      stopRecording: handleStopRecording,
      pauseResume: handlePauseResume,
      deleteRecording: handleDeleteRecording
    }));

    // This component doesn't render any UI elements
    return null;
  }
);

export default MyAudioRecorder;
